import PedidoManifestoController from "@/controller/PedidoManifestoController";

/**
 * Service responsável pela lógica de negócio dos produtos do manifesto
 * Segue o Single Responsibility Principle - apenas lógica de produtos
 */
export default class ProdutoManifestoService {

  /**
   * Processa arquivo XML completo
   * @param {File} file - Arquivo XML
   * @param {Object} empresaData - Dados da empresa
   * @param {Object} configuracaoEmpresa - Configuração da empresa
   * @returns {Promise<Object>} - Resultado do processamento
   */
  static async processarArquivoXML(file, empresaData, configuracaoEmpresa) {
    if (!file) {
      throw new Error("Arquivo XML não informado");
    }

    const formData = new FormData();
    formData.append("arquivo", file);
    formData.append("empresa_id", empresaData?.id || "");
    formData.append(
      "configuracao_empresa",
      JSON.stringify(configuracaoEmpresa || {})
    );

    const resultado = await PedidoManifestoController.importarNota(formData);

    if (!resultado || !resultado.produtos) {
      throw new Error("Resposta do servidor inválida ao processar XML");
    }

    const produtosAjustados = this.preencherDadosEmpresaPadrao(resultado.produtos);

    return {
      produtos: produtosAjustados,
      fornecedor: resultado.fornecedor,
      total_produtos: resultado.produtos.length,
      produtos_existentes: resultado.produtos_existentes || 0,
      produtos_novos: resultado.produtos_novos || 0,
    };
  }
  static aplicarGrupoPadrao(configuracaoEmpresa) {
    if (configuracaoEmpresa && configuracaoEmpresa.grupo_id) {
      console.log(`Aplicando grupo padrão da configuração: ${configuracaoEmpresa.grupo_id}`);
      return configuracaoEmpresa.grupo_id;
    } else {
      console.warn('Configuração da empresa não possui grupo_id');
      return null;
    }
  }

  static async buscarRelacionamentoExistente(empresaId, codFornec, nomeProduto) {
    try {
      const response = await PedidoManifestoController.buscarRelacionamento(
        empresaId,
        codFornec,
        nomeProduto
      );
      
      return response && response.produto ? response : null;
    } catch (error) {
      if (error.response && error.response.status !== 404) {
        console.error(`Erro ao buscar produto ${codFornec}:`, error.message);
      }
      return null;
    }
  }

  static aplicarDadosProdutoExistente(produto, relacionamentoExistente) {
    const produtoEmpresa = relacionamentoExistente.produto;
    
    produto.cod_empresa = produtoEmpresa?.produto_cod_int || produtoEmpresa?.codbarras || produtoEmpresa?.produto_cod || "";
    produto.nome_empresa = produtoEmpresa?.produto_nome || "";
    produto.fator_conv = relacionamentoExistente.fornecedor_fator || "1.00";
    produto.relacionamento_id = relacionamentoExistente.id;
    produto.produto_id = relacionamentoExistente.produto_id;
    produto.status = "existente";
    produto.produto_dados = produtoEmpresa;
    
    // Se o produto existente tem grupo_id, sobrescreve o da configuração
    if (produtoEmpresa.grupo_id) {
      produto.grupo_id = produtoEmpresa.grupo_id;
      console.log(`Grupo sobrescrito com o do produto existente: ${produtoEmpresa.grupo_id}`);
    }
  }

  static aplicarDadosProdutoNovo(produto) {
    produto.cod_empresa = "";
    produto.nome_empresa = "";
    produto.fator_conv = "1.00";
    produto.relacionamento_id = null;
    produto.produto_id = null;
    produto.status = "novo";
    produto.produto_dados = null;
    
    console.log(`Produto novo: ${produto.cod_fornec} - mantém grupo da configuração: ${produto.grupo_id}`);
  }

  /**
   * Cria estrutura de produto para API
   * @param {Object} produto - Produto do manifesto
   * @returns {Object} - Dados formatados para API
   */
  static criarEstruturaProdutoParaAPI(produto) {
    // Usa cod_empresa como codbarras (obrigatório), com fallback para cod_fornec
    const codbarras = produto.cod_empresa && produto.cod_empresa.toString().trim() !== ''
      ? produto.cod_empresa.toString().trim()
      : produto.cod_fornec;
    
    return {
      codbarras: codbarras,
      produto_nome: produto.dados_xml.xProd,
      produto_descricao: produto.dados_xml.xProd,
      um: produto.dados_xml.uCom,
      preco_custo: parseFloat(produto.dados_xml.vUnCom) || 0,
      preco: parseFloat(produto.dados_xml.vUnCom) || 0,
      preco_atacado: parseFloat(produto.dados_xml.vUnCom) || 0,
      produto_ncm: produto.dados_xml.ncm || "",
      produto_picms: parseFloat(produto.dados_xml.icms.picms) || 0,
      produto_cst: produto.dados_xml.icms.cst || "00",
      produto_cstpis: produto.dados_xml.icms.cstpis || "00",
      produto_cstcofins: produto.dados_xml.icms.cstcofins || "00",
      produto_orig: produto.dados_xml.icms.orig || "0",
      produto_cfop: produto.dados_xml.cfop || "",
      // Campos obrigatórios adicionais
      produto_cest: produto.dados_xml.icms.cest || "0",
      produto_class_sped: produto.dados_xml.icms.class_sped || "00",
      produto_cstipi: produto.dados_xml.icms.cstipi || "00",
      produto_pbc_pis: parseFloat(produto.dados_xml.icms.pbc_pis) || 100,
      produto_pbc_cofins: parseFloat(produto.dados_xml.icms.pbc_cofins) || 100,
      produto_pbc_icms: parseFloat(produto.dados_xml.icms.pbc_icms) || 100,
      produto_pbc_ipi: parseFloat(produto.dados_xml.icms.pbc_ipi) || 100,
      produto_pcofins: parseFloat(produto.dados_xml.icms.pcofins) || 100,
      produto_ppis: parseFloat(produto.dados_xml.icms.ppis) || 100,
      produto_pipi: parseFloat(produto.dados_xml.icms.pipi) || 100,
      ativo: 1,
      grupo_id: this.obterGrupoIdFinal(produto),
      empresa_id: produto.empresa_id,
      produto_fatexport: 1,
      produto_estminimo: 1,
      // Campos obrigatórios com valores padrão
      unidade_id: 1,
      idtipo: 1,
      qtd: 0,
      produto_finalidade: 1,
      produto_margemlucro: 0,
      produto_m_s: "M",
      produto_controla_estoque: 1,
      produto_balanca: 0,
      produto_produzido: 0,
      produto_materiaprima: 0
    };
  }

  /**
   * Obtém o grupo_id final para o produto
   * @param {Object} produto - Produto do manifesto
   * @returns {number|null} - ID do grupo final
   */
  static obterGrupoIdFinal(produto) {
    // Prioridade 1: Grupo selecionado pelo usuário
    if (produto.grupo_selecionado && produto.grupo_selecionado.id) {
      console.log('Usando grupo selecionado pelo usuário:', produto.grupo_selecionado.id);
      return produto.grupo_selecionado.id;
    }
    
    // Prioridade 2: Grupo definido no processamento
    if (produto.grupo_id) {
      console.log('Usando grupo_id do processamento:', produto.grupo_id);
      return produto.grupo_id;
    }
    
    console.warn('Nenhum grupo_id encontrado para o produto');
    return null;
  }

  /**
   * Cria estrutura de relacionamento para API
   * @param {Object} produto - Produto do manifesto
   * @param {number} fornecedorId - ID do fornecedor
   * @param {number} empresaId - ID da empresa
   * @returns {Object} - Dados formatados para API
   */
  static criarEstruturaRelacionamento(produto, fornecedorId, empresaId) {
    return {
      produto_id: produto.produto_id,
      pessoa_id: fornecedorId,
      cod_externo: produto.cod_fornec,
      fornecedor_descricao: produto.dados_xml.xProd,
      empresa_id: empresaId,
      fornecedor_um: produto.unidade_fornecedor || "",
      fornecedor_fator: parseFloat(produto.fator_conv) || 1.0
    };
  }

  /**
   * Filtra produtos por status
   * @param {Array} produtos - Lista de produtos
   * @param {string} status - Status desejado
   * @returns {Array} - Produtos filtrados
   */
  static filtrarProdutosPorStatus(produtos, status) {
    return produtos.filter(p => p.status === status);
  }

  /**
   * Aplica grupo padrão para produtos sem grupo
   * @param {Array} produtos - Lista de produtos
   * @param {Object} configuracaoEmpresa - Configuração da empresa
   * @returns {Array} - Produtos com grupos aplicados
   */
  static aplicarGruposPadraoParaProdutos(produtos, configuracaoEmpresa) {
    return produtos.map(produto => {
      if (!produto.grupo_id) {
        if (produto.status === "existente" && produto.produto_dados && produto.produto_dados.grupo_id) {
          produto.grupo_id = produto.produto_dados.grupo_id;
          console.log(`Aplicado grupo_id do produto existente: ${produto.produto_dados.grupo_id}`);
        } else if (produto.status === "novo" && configuracaoEmpresa && configuracaoEmpresa.grupo_id) {
          produto.grupo_id = configuracaoEmpresa.grupo_id;
          console.log(`Aplicado grupo_id da configuração: ${configuracaoEmpresa.grupo_id}`);
        }
      }
      return produto;
    });
  }

  /**
   * Prepara produtos para criação em lote
   * @param {Array} produtos - Lista de produtos
   * @param {number} empresaId - ID da empresa
   * @returns {Array} - Produtos formatados para API
   */
  static prepararProdutosParaCriacao(produtos, empresaId) {
    const produtosNovos = this.filtrarProdutosPorStatus(produtos, 'novo');
    
    return produtosNovos
      .filter(produto => produto.cod_fornec && produto.dados_xml.xProd)
      .map(produto => {
        const produtoFormatado = this.criarEstruturaProdutoParaAPI(produto);
        produtoFormatado.empresa_id = empresaId;
        return produtoFormatado;
      });
  }

  /**
   * Prepara relacionamentos para criação em lote
   * @param {Array} produtos - Lista de produtos
   * @param {number} fornecedorId - ID do fornecedor
   * @param {number} empresaId - ID da empresa
   * @returns {Array} - Relacionamentos formatados para API
   */
  static prepararRelacionamentosParaCriacao(produtos, fornecedorId, empresaId) {
    return produtos
      .filter(produto => produto.produto_id && !produto.relacionamento_existente)
      .map(produto => this.criarEstruturaRelacionamento(produto, fornecedorId, empresaId));
  }

  /**
   * Atualiza produtos com IDs dos produtos criados
   * @param {Array} produtos - Lista de produtos
   * @param {Array} produtosCriados - Produtos criados pela API
   * @returns {Array} - Produtos atualizados
   */
  static atualizarProdutosComIDs(produtos, produtosCriados) {
    return produtos.map(produto => {
      if (produto.status === 'novo' && !produto.produto_id) {
        const produtoCriado = produtosCriados.find(p => p.codbarras === produto.cod_fornec);
        if (produtoCriado) {
          produto.produto_id = produtoCriado.id;
          console.log(`Produto ${produto.cod_fornec} criado com ID: ${produtoCriado.id}`);
        }
      }
      return produto;
    });
  }

  /**
   * Atualiza status dos produtos após salvamento
   * @param {Array} produtos - Lista de produtos
   * @param {Array} relacionamentosCriados - Relacionamentos criados
   * @returns {Array} - Produtos com status atualizado
   */
  static atualizarStatusProdutos(produtos, relacionamentosCriados) {
    return produtos.map(produto => {
      const relacionamentoCriado = relacionamentosCriados.find(r => 
        r.cod_externo === produto.cod_fornec && 
        r.fornecedor_descricao === produto.dados_xml.xProd
      );
      
      if (relacionamentoCriado) {
        produto.status = "salvo";
        produto.relacionamento_id = relacionamentoCriado.id;
        console.log(`Produto ${produto.cod_fornec} salvo com relacionamento ID: ${relacionamentoCriado.id}`);
      }
      
      return produto;
    });
  }

  static preencherDadosEmpresaPadrao(produtos) {
    if (!Array.isArray(produtos)) {
      return [];
    }

    const possuiTexto = (valor) => typeof valor === "string" && valor.trim() !== "";

    const obterPrimeiroTexto = (...valores) => {
      for (const valor of valores) {
        if (possuiTexto(valor)) {
          return valor.trim();
        }
      }
      return "";
    };

    return produtos.map((produto) => {
      if (!produto) {
        return produto;
      }

      const status = typeof produto.status === "string" ? produto.status.toLowerCase().trim() : "";
      const produtoPossuiRelacionamento = Boolean(produto.relacionamento_id || produto.produto_id);
      const produtoNovo = status === "novo" || (!produtoPossuiRelacionamento && status !== "existente" && status !== "salvo");

      if (!produtoNovo) {
        return produto;
      }

      const codigoXml = obterPrimeiroTexto(
        produto.cod_empresa,
        produto.cod_empresa_xml,
        produto.cod_fornec,
        produto?.dados_xml?.cProd,
        produto?.dadosXml?.cProd,
        produto?.dadosXML?.cProd
      );

      const nomeXml = obterPrimeiroTexto(
        produto.nome_empresa,
        produto.nome_empresa_xml,
        produto?.dados_xml?.xProd,
        produto?.dadosXml?.xProd,
        produto?.dadosXML?.xProd
      );

      if (!possuiTexto(codigoXml) && !possuiTexto(nomeXml)) {
        return produto;
      }

      return {
        ...produto,
        cod_empresa: possuiTexto(codigoXml) ? codigoXml : produto.cod_empresa,
        nome_empresa: possuiTexto(nomeXml) ? nomeXml : produto.nome_empresa
      };
    });
  }

  /**
   * Valida se produto pode ser processado
   * @param {Object} produto - Produto para validar
   * @returns {boolean} - Se o produto é válido
   */
  static validarProduto(produto) {
    if (!produto) return false;
    if (!produto.cod_fornec) return false;
    if (!produto.dados_xml || !produto.dados_xml.xProd) return false;
    
    return true;
  }

  /**
   * Gera código da empresa para produtos novos
   * @param {string} codFornec - Código do fornecedor
   * @returns {string} - Código gerado
   */
  static gerarCodigoEmpresa(codFornec) {
    return `IMP_${codFornec}`;
  }
}
